import { AfterViewInit, Component, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { DxDataGridComponent } from 'devextreme-angular/ui/data-grid';
import CustomStore from 'devextreme/data/custom_store';
import { DataSourceOptions } from 'devextreme/data/data_source';
import { LoadOptions } from 'devextreme/data/load_options';
import { DxToolbarPreparingEvent } from '../../../../core/dx.types';
import { CommonService } from '../../../../shared/modules/my-common/services/common.service';
import { ConfigService } from '../../../../shared/modules/my-common/services/config.service';
import { DataSourceService } from '../../../../shared/modules/my-common/services/datasource.service';
import { StateStoreService } from '../../../../shared/modules/my-common/services/state-store.service';
import { ABaseComponent } from '../../../../shared/modules/ui/components/abstract/a-base.component';
import { GridHelperService } from '../../../../shared/modules/ui/services/grid-helper.service';
import { UiService } from '../../../../shared/modules/ui/services/ui.service';
import { ConsumerApi, LoggerService } from '../../../../shared/sdk';
import { MongoLoadOptionsConverter } from 'src/app/shared/classes/loopback-custom-store/generic/load-options-converters/MongoLoadOptionsConverter';
import { BehaviorSubject, of } from 'rxjs';
import { delay, exhaustMap, takeUntil, tap } from 'rxjs/operators';
import moment from 'moment';

@Component({
  selector: 'app-expiring-trips',
  templateUrl: './expiring-trips.component.html',
  styleUrls: ['./expiring-trips.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ExpiringTripsComponent extends ABaseComponent implements OnInit, AfterViewInit {
  // isSU$: Observable<boolean>;

  $resize$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  @ViewChild(DxDataGridComponent, { static: true }) grid: DxDataGridComponent;
  // grid_stateStoring: any;

  dso: DataSourceOptions;

  columns: any[] = [];

  startDate: Date = moment().subtract(15, 'days').toDate();
  endDate: Date = moment().add(15, 'days').toDate();
  today: Date = moment().toDate();
  showSignatureOnFile = true;
  expiringFromOptions: { name: string; value: number }[] = [];
  expiringFrom: number | null = null;

  get minDate() {
    return this.endDate ? moment(this.endDate).subtract(2, 'months').toDate() : null;
  }

  get maxDate() {
    return this.startDate ? moment(this.startDate).add(2, 'months').toDate() : null;
  }

  constructor(
    public logger: LoggerService,
    public config: ConfigService,
    public common: CommonService,
    private consumerApi: ConsumerApi,
    private gridHelper: GridHelperService,
  ) {
    super(logger);

    // this.grid_stateStoring = {
    //   enabled: true,
    //   type: 'localStorage',
    //   storageKey: '7276d037-36fa-5e37-ab23-32176ee1b3b1',
    // };

    this.$resize$
      .pipe(
        exhaustMap(() =>
          of(true).pipe(
            delay(1000),
            tap(() => {
              this.grid && this.grid.instance && this.grid.instance.repaint();
            }),
          ),
        ),
        takeUntil(this.$onDestroy$),
      )
      .subscribe();
  }

  ngOnInit() {
    super.ngOnInit();
    this.dso = this.buildDataSource();
    this.rebuildColumns();
    this.generateLastExpiredDays();
  }

  getTripsClass(cellInfo) {
    if (cellInfo.value === 0) return 'red';
    const caption = cellInfo.column.caption;
    const d = moment(caption, 'MM/DD').format('YYYY-MM-DD');
    const isSign = !caption.includes('.');
    const days = isSign ? cellInfo.data.signDays : cellInfo.data.futureDays;
    const trips = days && days[d] && days[d].trips;
    if (!trips || !trips.length) return '';
    if (trips.find(t => t.tripStatus[0] === 'S')) return 'green';
    return 'red';
  }

  getTripsTooltip(cellInfo: any): string {
    const caption = cellInfo.column.caption;
    const d = moment(caption, 'MM/DD').format('YYYY-MM-DD');
    const isSign = !caption.includes('.');
    const days = isSign ? cellInfo.data.signDays : cellInfo.data.futureDays;
    const trips = days && days[d] && days[d].trips;
    if (!trips || !trips.length) return '';
    return trips
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .map(t => `${t.tripId}: ${t.tripStatus || ''}`)
      .join('\n');
  }

  handleChange() {
    if (!this.startDate || !this.endDate) return;
    this.rebuildColumns();
    this.generateLastExpiredDays();
    this.grid.instance.refresh();
  }

  handleExpiringFromChanged() {
    if (!this.startDate || !this.endDate) return;
    this.grid.instance.refresh();
  }

  handleSignatureOnFileChange() {
    this.grid.instance.refresh();
  }

  generateLastExpiredDays() {
    if (!this.endDate) return;
    const start = moment(this.startDate).startOf('day');
    const end = moment(this.endDate);
    const diff = end.diff(start, 'days') + 1;
    this.expiringFrom = null;
    this.expiringFromOptions = Array.from({ length: diff }, (_, i) => ({
      name: start.clone().add(i, 'days').format('MM/DD'),
      value: i,
    }));
  }

  rebuildColumns() {
    this.columns = [
      { dataField: 'person_firstname', caption: 'First Name' },
      { dataField: 'person_lastname', caption: 'Last Name' },
      { dataField: 'mci', caption: 'MCI' },
      { dataField: 'person_dob', caption: 'DOB', dataType: 'date' },
      { dataField: 'activeMco', caption: 'Active MCO' },
      { dataField: 'lastEligibility', caption: 'Last Eligibility' },
      {
        dataField: 'lastEligibilityDate',
        caption: 'Last Eligibility Date',
        calculateCellValue: row => (row.lastEligibilityDate ? moment(row.lastEligibilityDate).format('MMMM') : ''),
      },
    ];

    const diff = moment(this.endDate).diff(moment(this.startDate).startOf('day'), 'days');
    for (let i = 0; i < diff + 1; i++) {
      const d = moment(this.startDate).add(i, 'days');
      if (d.isBefore(moment(this.today).endOf('day'))) {
        this.columns.push({
          dataField: `signDays[${d.format('YYYY-MM-DD')}].tripsCount`,
          caption: `${d.format('MM/DD')}`,
          cellTemplate: 'trips',
        });
      }

      if (d.isAfter(moment(this.today).startOf('day'))) {
        this.columns.push({
          dataField: `futureDays[${d.format('YYYY-MM-DD')}].tripsCount`,
          caption: `${d.format('MM/DD')}.`,
          cellTemplate: 'trips',
          cssClass: 'future-column',
        });
      }
    }
  }

  repaint(): void {
    this.grid && this.grid.instance && this.grid.instance.repaint();
  }

  grid_onInitialized(e) {
    // !!! important to handle grid initialization, it will set up the grid options and styles
    this.gridHelper.handle(e.component, {
      notifyErrors: true,
    });
  }

  grid_onToolbarPreparing(e: DxToolbarPreparingEvent) {}

  ngAfterViewInit(): void {}

  private buildDataSource() {
    const store = new CustomStore({
      useDefaultSearch: true,
      cacheRawData: false,
      load: async (loadOptions: LoadOptions): Promise<any> => {
        let q = new MongoLoadOptionsConverter().convert(loadOptions);
        // if (Object.keys(q).length === 0) return [];
        const start = moment(this.startDate).format('YYYY-MM-DD');
        const end = moment(this.endDate).format('YYYY-MM-DD');
        const diff = moment(this.endDate).diff(moment(this.startDate), 'days');
        const expiringFrom = this.expiringFrom !== null ? this.expiringFrom : Math.floor(diff - diff / 3);
        return this.consumerApi.getExpiringTrips({ q }, start, end, expiringFrom, this.showSignatureOnFile).toPromise();
      },
    });

    return { store } as DataSourceOptions;
  }
}
